import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Card, CardBody, Container } from "reactstrap";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { toast } from "react-toastify";

import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import {
  getExternalLeadsMetadata,
  submitExternalLeadsFilterData,
} from "model/ExternalLeadsModel";
import ExternalLeadViewModal from "pages/AllCommonModal/ExternalLeadViewModal/external-lead-view-modal";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import {
  buildEmptyFilterFromMeta,
  countActiveMetadataFilters,
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  isMetadataFilterDefault,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeExternalLeadsFilterPayload = (raw = {}, filterMeta = []) => {
  const out = { ...raw };
  const pageNumber = Number(out.pageNumber) || 1;
  const pageSize = Number(out.pageSize) || DEFAULT_PAGE_SIZE;
  const dateFields = new Set(
    filterMeta.filter((f) => f?.type === "date" && f?.field).map((f) => f.field)
  );

  const next = { pageNumber, pageSize };
  Object.keys(out).forEach((key) => {
    if (key === "pageNumber" || key === "pageSize") return;
    let v = out[key];
    if (v === "" || v === undefined) {
      next[key] = null;
      return;
    }
    if (dateFields.has(key)) {
      next[key] = normalizeMetadataFilterDateForApi(v);
      return;
    }
    if (typeof v === "string") {
      next[key] = strOrNull(v);
      return;
    }
    next[key] = v;
  });
  Object.keys(next).forEach((payloadKey) => {
    if (isAsyncFilterLabelFieldKey(payloadKey)) {
      delete next[payloadKey];
    }
  });
  return next;
};

const ExternalLeads = () => {
  const { fetchMetadataSchema, getDropdownOptionsByApiKey } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("external-leads");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [externalLeadsListFilter, setExternalLeadsListFilter] = useState({});
  const [externalLeadsData, setExternalLeadsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [externalLeadsFilterMetaData, setExternalLeadsFilterMetaData] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalLeadId, setViewModalLeadId] = useState("");

  const [toolbarDropdownReactSelectOptionsByFieldName, setToolbarDropdownReactSelectOptionsByFieldName] =
    useState({});

  const defaultTableColumns = useMemo(
    () => [
      { Header: "Client", accessor: "client" },
      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "Status", accessor: "status" },
    ],
    []
  );

  const externalLeadsMetadataToolbarFilterDefinitions = useMemo(
    () =>
      (Array.isArray(externalLeadsFilterMetaData) ? externalLeadsFilterMetaData : []).filter(
        (filterDefinitionRow) =>
          filterDefinitionRow?.field && isMetadataFilterDefault(filterDefinitionRow)
      ),
    [externalLeadsFilterMetaData]
  );

  const externalLeadsMetadataModalOnlyFilterDefinitions = useMemo(
    () =>
      (Array.isArray(externalLeadsFilterMetaData) ? externalLeadsFilterMetaData : []).filter(
        (filterDefinitionRow) =>
          filterDefinitionRow?.field && !isMetadataFilterDefault(filterDefinitionRow)
      ),
    [externalLeadsFilterMetaData]
  );

  const usesMetadataDrivenExternalLeadsFilterUi =
    Array.isArray(externalLeadsFilterMetaData) && externalLeadsFilterMetaData.length > 0;

  const shouldShowTableHeaderFilterAndResetControls = useMemo(
    () =>
      !usesMetadataDrivenExternalLeadsFilterUi ||
      externalLeadsMetadataModalOnlyFilterDefinitions.length > 0,
    [usesMetadataDrivenExternalLeadsFilterUi, externalLeadsMetadataModalOnlyFilterDefinitions]
  );

  const activeModalFilterCount = useMemo(
    () =>
      countActiveMetadataFilters(
        externalLeadsListFilter,
        externalLeadsMetadataModalOnlyFilterDefinitions
      ),
    [externalLeadsListFilter, externalLeadsMetadataModalOnlyFilterDefinitions]
  );

  const shouldRenderExternalLeadsMetadataToolbarFilterRow =
    externalLeadsMetadataToolbarFilterDefinitions.length > 0;

  const buildBaselineEmptyExternalLeadsListFilterFromMetadata = useCallback(
    () => buildEmptyFilterFromMeta(externalLeadsFilterMetaData),
    [externalLeadsFilterMetaData]
  );

  const externalLeadsFilterModalData = useMemo(() => {
    if (externalLeadsMetadataModalOnlyFilterDefinitions.length) {
      return mapFilterDefinitionsToModalData(
        externalLeadsMetadataModalOnlyFilterDefinitions,
        externalLeadsListFilter
      );
    }

    return [];
  }, [externalLeadsListFilter, externalLeadsMetadataModalOnlyFilterDefinitions]);

  useEffect(() => {
    if (!Array.isArray(externalLeadsFilterMetaData) || !externalLeadsFilterMetaData.length) return;
    setExternalLeadsListFilter((previousFilter) => {
      const mergedFilter = { ...previousFilter };
      let mergedNewKeysIntoState = false;
      externalLeadsFilterMetaData.forEach((filterDefinitionRow) => {
        const backendFilterFieldIdentifier = filterDefinitionRow?.field;
        if (!backendFilterFieldIdentifier) return;
        if (!(backendFilterFieldIdentifier in mergedFilter)) {
          mergedFilter[backendFilterFieldIdentifier] =
            filterDefinitionRow?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(backendFilterFieldIdentifier);
          if (!(asyncLabelFieldKey in mergedFilter)) {
            mergedFilter[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilter : previousFilter;
    });
  }, [externalLeadsFilterMetaData]);

  useEffect(() => {
    const toolbarDropdownDefinitions = externalLeadsMetadataToolbarFilterDefinitions.filter(
      (definitionRow) =>
        definitionRow?.type === "dropdown" && definitionRow?.field
    );
    if (!toolbarDropdownDefinitions.length) {
      setToolbarDropdownReactSelectOptionsByFieldName({});
      return;
    }
    let cancelled = false;
    (async () => {
      const tuples = await Promise.all(
        toolbarDropdownDefinitions.map(async (definitionRow) => {
          const fieldKey = definitionRow.field;
          const staticOptions =
            Array.isArray(definitionRow?.itemArray) ? definitionRow.itemArray : [];
          if (staticOptions.length) return [fieldKey, staticOptions];
          const fetched = await getDropdownOptionsByApiKey(definitionRow?.api);
          return [fieldKey, fetched];
        })
      );
      if (cancelled) return;
      const byField = {};
      tuples.forEach(([fieldKey, options]) => {
        byField[fieldKey] = options;
      });
      setToolbarDropdownReactSelectOptionsByFieldName(byField);
    })();
    return () => {
      cancelled = true;
    };
  }, [externalLeadsMetadataToolbarFilterDefinitions, getDropdownOptionsByApiKey]);

  const mapRecordsToRows = (list = []) =>
    list.map((item = {}) => ({
      ...item,
      _originalExternalLead: { ...item },
      id: item?.id || item?._id || "",
    }));

  const buildPayloadFromFilterForm = (form = {}, overrides = {}) =>
    normalizeExternalLeadsFilterPayload(
      {
        pageNumber: 1,
        pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
        ...form,
        ...overrides,
      },
      externalLeadsFilterMetaData
    );

  const getExternalLeadsList = async (payloadOverrides = {}, options = {}) => {
    const { onlyPagination = false, filterMeta: filterMetaOption } = options;
    const filterMeta = filterMetaOption ?? externalLeadsFilterMetaData;
    setLoading(true);
    try {
      const payload = onlyPagination
        ? {
            pageNumber: Number(payloadOverrides.pageNumber) || 1,
            pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
          }
        : normalizeExternalLeadsFilterPayload(
            {
              pageNumber: requestPayload.pageNumber,
              pageSize: requestPayload.pageSize,
              ...requestPayload,
              ...payloadOverrides,
            },
            filterMeta
          );

      const response = await submitExternalLeadsFilterData(payload);
      const responseData = response?.data ?? response ?? {};
      const listData =
        responseData?.records ??
        responseData?.data ??
        (Array.isArray(responseData) ? responseData : []);

      const records = Array.isArray(listData) ? listData : [];
      setExternalLeadsData(mapRecordsToRows(records));

      if (response == null || response?.success !== false) {
        setPaginationMeta({
          pageNumber:
            Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });
        if (onlyPagination) {
          setRequestPayload({
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
          });
          setExternalLeadsListFilter(buildEmptyFilterFromMeta(filterMeta));
        } else {
          setRequestPayload(normalizeExternalLeadsFilterPayload(payload, filterMeta));
        }
      } else {
        setExternalLeadsData([]);
        if (response) {
          toast.error(response?.message || "Unable to load external leads list");
        }
      }
    } catch (err) {
      toast.error(err?.message || "Unable to load external leads list");
      setExternalLeadsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      let filters = [];
      try {
        const metaRes = await fetchMetadataSchema("metadata:externalLeads", () =>
          getExternalLeadsMetadata()
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        filters = Array.isArray(meta?.filters) ? meta.filters : [];

        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => {
              const accessor = pascalToCamel(col?.field);
              return {
                Header: col?.title,
                accessor,
                type: col?.type,
              };
            })
          : defaultTableColumns;
        setTableMetaData(tableColumns);
        setExternalLeadsFilterMetaData(filters);
        setExternalLeadsListFilter(buildEmptyFilterFromMeta(filters));
      } catch (e) {
        toast.error(e?.message || "Unable to load external leads metadata");
        setTableMetaData(defaultTableColumns);
        setExternalLeadsFilterMetaData([]);
        setExternalLeadsListFilter({});
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getExternalLeadsList(
      {
        pageNumber: 1,
        pageSize,
        ...buildEmptyFilterFromMeta(externalLeadsFilterMetaData),
      },
      { onlyPagination: false, filterMeta: externalLeadsFilterMetaData }
    );
  }, [settingsReady, settingsPageSize, metadataBootstrapDone, externalLeadsFilterMetaData]);

  const handleFilterSubmit = async (modalFormPayload = {}) => {
    const mergedFilter = mergeModalSubmitIntoListFilter(
      externalLeadsListFilter,
      externalLeadsFilterModalData,
      modalFormPayload
    );
    setExternalLeadsListFilter(mergedFilter);
    setCommonFilterModalOpen(false);
    const payload = buildPayloadFromFilterForm(mergedFilter);
    await getExternalLeadsList(payload);
  };

  const handleUpperToolbarExternalLeadsFilterSearch = async () => {
    const payloadFromToolbar = buildPayloadFromFilterForm(externalLeadsListFilter);
    await getExternalLeadsList(payloadFromToolbar);
  };

  const handleToolbarTextFilterInputChange = (backendFieldIdentifier, rawStringValueFromDom) => {
    setExternalLeadsListFilter((previousFilter) => ({
      ...previousFilter,
      [backendFieldIdentifier]: rawStringValueFromDom ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (
    backendFieldIdentifier,
    reactSelectSelectedOptionRow
  ) => {
    setExternalLeadsListFilter((previousFilter) => ({
      ...previousFilter,
      [backendFieldIdentifier]: reactSelectSelectedOptionRow?.value ?? "",
      [getAsyncFilterLabelFieldKey(backendFieldIdentifier)]:
        reactSelectSelectedOptionRow?.label ?? "",
    }));
  };

  const handleFilterReset = async () => {
    const resetFilter = buildBaselineEmptyExternalLeadsListFilterFromMetadata();
    setExternalLeadsListFilter(resetFilter);
    setCommonFilterModalOpen(false);
    await getExternalLeadsList(
      { pageNumber: 1, pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE },
      { onlyPagination: true }
    );
  };

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
    };
    run();
  }, [commonResetFilter]);

  const handlePageChange = async (page) => {
    await getExternalLeadsList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getExternalLeadsList({ pageNumber: 1, pageSize: rows });
  };

  const handleViewExternalLead = (row = {}) => {
    const leadId = String(row?.id ?? row?._id ?? "").trim();
    if (!leadId) {
      toast.error("Record ID not found");
      return;
    }
    setViewModalLeadId(leadId);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewModalLeadId("");
  };

  const filteredData = useMemo(() => externalLeadsData, [externalLeadsData]);

  return (
    <div className="page-content external-leads-page">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">External Leads</h5>
            </div>
            <hr />

            {shouldRenderExternalLeadsMetadataToolbarFilterRow ? (
              <MetadataFilterToolbar
                toolbarDefinitions={externalLeadsMetadataToolbarFilterDefinitions}
                listFilter={externalLeadsListFilter}
                onTextChange={handleToolbarTextFilterInputChange}
                onSelectChange={handleToolbarReactSelectFilterChange}
                onSearch={handleUpperToolbarExternalLeadsFilterSearch}
                onClear={handleFilterReset}
                showClearWhenNoModalFilters={
                  !shouldShowTableHeaderFilterAndResetControls
                }
                toolbarDropdownOptionsByFieldName={
                  toolbarDropdownReactSelectOptionsByFieldName
                }
                layoutPreset="callExecutive"
                pageName="external-leads"
              />
            ) : null}

            {loading ? (
              <Loaders />
            ) : tableMetaData?.length ? (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={filteredData}
                isGlobalFilter={false}
                isSort={true}
                isActionRequired={true}
                onView={handleViewExternalLead}
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                pageName="external-leads"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={setCommonFilterModalOpen}
                setCommonResetFilter={setCommonResetFilter}
                allCommonFilter={
                  shouldShowTableHeaderFilterAndResetControls &&
                  externalLeadsFilterModalData.length > 0
                }
                activeFilterCount={activeModalFilterCount}
                isCheckboxLeftSide={false}
                enableBulkDeactivate={false}
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
          </CardBody>
        </Card>

        {externalLeadsFilterModalData.length > 0 && (
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="External Leads Filter"
            handleSubmit={handleFilterSubmit}
            filterData={externalLeadsFilterModalData}
            commonResetFilter={commonResetFilter}
            setCommonResetFilter={setCommonResetFilter}
            pageName="external-leads"
          />
        )}

        <ExternalLeadViewModal
          isOpen={viewModalOpen}
          toggle={closeViewModal}
          leadId={viewModalLeadId}
        />
      </Container>
    </div>
  );
};

ExternalLeads.propTypes = {
  orders: PropTypes.array,
};

export default withRouter(ExternalLeads);
